import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  effect,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router
} from '@angular/router';
import { AvatarTooltipUtil } from '@libs/common-services';
import { NotificationViewMode } from '@design-system/components/m-alert-item';
import {
  BrandPickerOption,
  BrandPickerSelectEvent
} from '@design-system/components/m-brand-picker';
import {
  TopNavAction,
  TopNavActionClickEvent,
  TopNavAvatar,
  TopNavBrandPicker,
  TopNavLogo
} from '@design-system/components/m-top-nav';
import { NotificationCenterService } from '@portal/app/dashboard/notification-center/notification-center.service';
import {
  dashboardURI,
  getSmartLandingDashboard,
  shouldLandOnAdminSettings,
  smartLandingDashboardURI
} from '@portal/app/dashboard/utils';
import {
  FeedbackModalComponent,
  FeedbackSubmitEvent
} from '@portal/app/shared/components/feedback-modal/feedback-modal.component';
import { DashboardLiteralIds } from '@portal/app/shared/constants';
import { UserModel } from '@portal/app/shared/models/userModel';
import { ApiService } from '@portal/app/shared/services/api.service';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { BrandRowsService } from '@portal/app/shared/services/brand-rows.service';
import { ConfigDashboardService } from '@portal/app/shared/services/config-dashboard.service';
import { DashboardService } from '@portal/app/shared/services/dashboard.service';
import { NavigationService } from '@portal/app/shared/services/navigation.service';
import { RecentBrandsService } from '@portal/app/shared/services/recent-brands.service';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { SentryService } from '@portal/app/shared/services/sentry.service';
import { TableauService } from '@portal/app/shared/services/tableau.service';
import { ViewService } from '@portal/app/shared/services/view.service';
import { AiPanelService } from '@portal/app/shared/services/ai-panel.service';
import {
  BrandRow,
  ConfigDashboardInstance,
  DisplayDashboard
} from '@portal/app/shared/types';
import { MenuItem } from 'primeng/api';
import { filter, map, startWith, take } from 'rxjs';

import { AVATAR_META, tableauExportItems } from './navbar.config';
import {
  buildSupportMenu,
  deriveInitials,
  isTableauUriPath,
  prependMimPageItem,
  prependMimSettingsItem,
  resolveMoreMeta,
  resolveSettingsMeta,
  toMegaMenuItems
} from './navbar.utils';
import { colors } from '@design-system/styles/colors';
import { urlMatches } from '@design-system/utils';

const ROUTER_URL_PREFIX = 'https://portal.measured.com/';
// Grouped recents/divider layout only kicks in once the flat list is long
// enough to be worth shortcutting (PRD: user has 4+ brands).
const RECENTS_MIN_BRANDS = 4;

@Component({
  selector: 'portal-navbar',
  templateUrl: './navbar.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent {
  @ViewChild('feedbackModal') public feedbackModal!: FeedbackModalComponent;

  readonly notificationViewMode = NotificationViewMode;
  readonly tableauExportItems: MenuItem[] = tableauExportItems((type) =>
    this.exportTableau(type)
  );

  readonly notificationCenterVisible = signal(false);
  readonly logOutModalIsVisible = signal(false);

  readonly user: UserModel = this.authService.getUser() as UserModel;

  readonly dashboards = toSignal(this.dashboardService.dashboards, {
    initialValue: [] as DisplayDashboard[]
  });

  readonly configDashboards = toSignal<Record<
    string,
    ConfigDashboardInstance
  > | null>(
    this.configDashboardService.getConfigDashboards().pipe(
      map((list) =>
        list.reduce<Record<string, ConfigDashboardInstance>>((acc, d) => {
          acc[d.literalId] = d;
          return acc;
        }, {})
      )
    ),
    { initialValue: null }
  );

  readonly brandRows = toSignal(this.brandRowsService.brandRows$, {
    initialValue: []
  });

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(
        (e): e is NavigationEnd | NavigationCancel | NavigationError =>
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError
      ),
      map((e) => `${ROUTER_URL_PREFIX}${e.url}`),
      startWith(location.href)
    ),
    { initialValue: location.href }
  );

  // Raw route path (not the prefixed currentUrl above) — used to resolve which
  // top-nav action is active. Active-state is app routing policy, so the
  // container owns it and passes plain booleans to the presentational m-top-nav.
  private readonly routePath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly actionAlertsCount = toSignal(
    this.notificationCenterService.actionAlertsCount$,
    { initialValue: 0 }
  );

  readonly unreadInfoAlertsCount = toSignal(
    this.notificationCenterService.unreadInfoAlertsCount$,
    { initialValue: 0 }
  );

  readonly brandLabel = computed(() => this.selectionService.selectionLabel());

  readonly clientId = computed(
    () => this.selectionService.clientBrandSignal().clientId
  );

  readonly brandId = computed(
    () => this.selectionService.clientBrandSignal().brandId
  );

  readonly brandProductPath = computed(() =>
    this.brandLabel()
      ? `${this.selectionService.selectionSlug()}/products`
      : 'products'
  );

  readonly showExport = computed(() => isTableauUriPath(this.currentUrl()));

  readonly userOptionsMenuItems = computed(() =>
    this.navigationService.createUserOptionsMenuItems(
      this.configDashboards() ?? {},
      (visible) => this.logOutModalIsVisible.set(visible)
    )
  );

  readonly settingsMenuItems = computed(() =>
    prependMimSettingsItem(
      this.navigationService.createSettingsMenuItems(
        this.configDashboards() ?? {},
        this.brandProductPath()
      ),
      this.dashboards(),
      this.brandProductPath()
    )
  );

  readonly supportMenuItems = buildSupportMenu(this.user, () =>
    this.openFeedbackModal()
  );

  readonly benchmarksLink = computed(() =>
    this.navigationService.getBenchmarksLink(
      this.dashboards(),
      this.brandProductPath()
    )
  );

  readonly crossChannelLink = computed(() =>
    this.navigationService.getCrossChannelLink(
      this.dashboards(),
      this.brandProductPath()
    )
  );

  readonly moreMenuItems = computed(() =>
    prependMimPageItem(
      this.navigationService.createMoreMenuItems(
        this.dashboards(),
        this.configDashboards() ?? {},
        this.brandProductPath()
      ),
      this.configDashboards() ?? {},
      this.selectionService.selectionSlug()
    )
  );

  readonly optimizeMenuItems = computed(() =>
    this.navigationService.createOptimizeMenuItems(
      this.dashboards(),
      this.brandProductPath()
    )
  );

  readonly optimizeMatchPaths = computed(() =>
    this.navigationService.getOptimizeMatchPaths(
      this.dashboards(),
      this.brandProductPath()
    )
  );

  readonly homepageDashboardLink = computed(() =>
    dashboardURI(
      this.brandProductPath(),
      this.dashboards(),
      this.user,
      DashboardLiteralIds.homePage
    )
  );

  readonly landingDashboardLink = computed(() =>
    smartLandingDashboardURI(
      this.brandProductPath(),
      this.dashboards(),
      this.user,
      this.configDashboards() ?? {}
    )
  );

  readonly geoTestsV2Link = computed(() =>
    this.navigationService.getGeoTestsV2Link(
      this.configDashboards() ?? {},
      this.brandProductPath()
    )
  );

  readonly logo = computed<TopNavLogo>(() => ({
    routerLink: this.landingDashboardLink(),
    alt: 'Measured'
  }));

  readonly recentBrands = computed<BrandPickerOption[]>(() => {
    const rows = this.brandRows();
    if (rows.length < RECENTS_MIN_BRANDS) {
      return [];
    }
    return this.recentBrandsService
      .keys()
      .map((key) =>
        rows.find(
          (row) => row.clientId === key.clientId && row.brandId === key.brandId
        )
      )
      .filter((row): row is BrandRow => !!row)
      .map((row) => ({
        clientId: row.clientId,
        brandId: row.brandId,
        name: row.name
      }));
  });

  readonly brandPicker = computed<TopNavBrandPicker | undefined>(() => {
    const label = this.brandLabel();
    if (!label) {
      return undefined;
    }
    return {
      label,
      ariaLabel: 'Change brand',
      brands: this.brandRows().map((row) => ({
        clientId: row.clientId,
        brandId: row.brandId,
        name: row.name
      })),
      recentBrands: this.recentBrands()
    };
  });

  readonly avatar = computed<TopNavAvatar | undefined>(() => {
    if (!this.user) {
      return undefined;
    }
    const rModId = this.user.getUsername?.() ?? '';
    return {
      initials: deriveInitials(this.user),
      ariaLabel: 'Account menu',
      bg: AvatarTooltipUtil.extractUserHeadshotBg({ rModId }),
      color: colors['gray-000'],
      megaMenuItems: toMegaMenuItems(this.userOptionsMenuItems(), (m) =>
        m.id ? AVATAR_META[m.id] : undefined
      )
    };
  });

  readonly primaryActions = computed<TopNavAction[]>(() =>
    this.buildPrimaryActions()
  );

  readonly secondaryActions = computed<TopNavAction[]>(() =>
    this.buildSecondaryActions()
  );

  readonly trailingAction = computed<TopNavAction | undefined>(() => {
    if (!this.showExport()) {
      return undefined;
    }
    return {
      id: 'export',
      iconName: 'save_alt',
      label: 'Export',
      menuItems: this.tableauExportItems
    };
  });

  private hasRedirected = false;

  private readonly bootstrap = effect(() => {
    const dashboards = this.dashboards();
    const configs = this.configDashboards();
    if (this.hasRedirected || !dashboards.length || configs == null) {
      return;
    }
    this.hasRedirected = true;
    this.redirectToLandingIfApplicable();
  });

  constructor(
    private readonly tableauService: TableauService,
    private readonly dashboardService: DashboardService,
    private readonly configDashboardService: ConfigDashboardService,
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly brandRowsService: BrandRowsService,
    private readonly selectionService: SelectionService,
    private readonly viewService: ViewService,
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly sentryService: SentryService,
    protected readonly aiPanelService: AiPanelService,
    private readonly recentBrandsService: RecentBrandsService
  ) {}

  openFeedbackModal(): void {
    this.feedbackModal.open();
  }

  async onFeedbackSubmit(data: FeedbackSubmitEvent): Promise<void> {
    const success = await this.sentryService.submitFeedback(
      data.message,
      data.category,
      data.screenshot,
      {
        email: this.user?.getEmail(),
        name: this.user?.getName() || this.user?.getUsername()
      }
    );
    this.feedbackModal.onSubmitComplete(success);
  }

  toggleNotificationCenter(event: Event): void {
    event.stopPropagation();
    this.notificationCenterVisible.update((open) => !open);
  }

  onNotificationCenterClosed(): void {
    this.notificationCenterVisible.set(false);
  }

  exportTableau(type: string): void {
    this.tableauService
      .changeExport(type)
      .pipe(take(1))
      .subscribe({ error: (err) => console.error(err) });
  }

  onActionClicked({ action, event }: TopNavActionClickEvent): void {
    switch (action.id) {
      case 'notifications':
        this.toggleNotificationCenter(event);
        break;
      case 'mia':
        this.aiPanelService.toggle();
        break;
      default:
        break;
    }
  }

  onBrandPickerOpened(): void {
    this.brandRowsService.refresh();
  }

  onBrandSelected({ brand }: BrandPickerSelectEvent): void {
    if (
      brand.clientId === this.clientId() &&
      brand.brandId === this.brandId()
    ) {
      return;
    }
    const row = this.brandRows().find(
      (r) => r.clientId === brand.clientId && r.brandId === brand.brandId
    );
    if (!row || !this.user) {
      return;
    }
    this.recentBrandsService.record({
      clientId: brand.clientId,
      brandId: brand.brandId
    });
    this.apiService.cancelPendingRequest.next(true);
    this.viewService.switchBrand(row, this.user);
    sessionStorage.removeItem('ddi-settings');
    sessionStorage.removeItem('ddi-conversion-types');
  }

  private isPathActive(matchPaths: string[] | undefined): boolean {
    const path = this.routePath();
    return !!matchPaths?.some((p) => urlMatches(path, p));
  }

  private buildPrimaryActions(): TopNavAction[] {
    const actions: TopNavAction[] = [];
    const homepage = this.homepageDashboardLink();
    if (homepage && homepage.includes(DashboardLiteralIds.homePage)) {
      actions.push({
        id: 'home',
        label: 'Home',
        routerLink: homepage,
        active: this.isPathActive([homepage])
      });
    }
    const crossChannel = this.crossChannelLink();
    if (crossChannel) {
      actions.push({
        id: 'analyze',
        label: 'Analyze',
        routerLink: crossChannel,
        active: this.isPathActive([crossChannel])
      });
    }
    const experiment = this.geoTestsV2Link();
    if (experiment) {
      actions.push({
        id: 'experiment',
        label: 'Experiment',
        routerLink: experiment,
        active: this.isPathActive([experiment])
      });
    }
    const optimize = this.optimizeMenuItems();
    const matchPaths = this.optimizeMatchPaths();
    const [single] = optimize;
    if (optimize.length === 1 && single) {
      actions.push({
        id: 'optimize',
        label: 'Optimize',
        routerLink: single.routerLink,
        queryParams: single.queryParams,
        active: this.isPathActive(matchPaths)
      });
    } else if (optimize.length > 1) {
      actions.push({
        id: 'optimize',
        label: 'Optimize',
        megaMenuItems: toMegaMenuItems(optimize),
        active: this.isPathActive(matchPaths)
      });
    }
    const benchmarks = this.benchmarksLink();
    if (benchmarks) {
      actions.push({
        id: 'benchmarks',
        label: 'Benchmarks',
        routerLink: benchmarks,
        active: this.isPathActive([benchmarks])
      });
    }
    const more = this.moreMenuItems();
    if (more.length) {
      actions.push({
        id: 'more',
        label: 'More',
        megaMenuItems: toMegaMenuItems(more, resolveMoreMeta),
        megaMenuColumns: 2
      });
    }
    if (this.aiPanelService.isEnabled()) {
      actions.push({
        id: 'mia',
        iconSrc: 'assets/icons/ai-sparkle.svg',
        label: 'Mia',
        ariaLabel: 'Toggle Mia',
        open: this.aiPanelService.visible()
      });
    }
    return actions;
  }

  private buildSecondaryActions(): TopNavAction[] {
    const actionAlerts = this.actionAlertsCount();
    const unreadInfo = this.unreadInfoAlertsCount();
    return [
      {
        id: 'notifications',
        iconName: 'notifications_active',
        ariaLabel: 'Notifications',
        open: this.notificationCenterVisible(),
        notification: {
          count: actionAlerts,
          showDot: unreadInfo > 0 || actionAlerts > 0
        }
      },
      {
        id: 'settings',
        iconName: 'build',
        ariaLabel: 'Settings',
        megaMenuItems: toMegaMenuItems(
          this.settingsMenuItems(),
          resolveSettingsMeta
        ),
        megaMenuColumns: 2,
        hideArrow: true
      },
      {
        id: 'support',
        iconName: 'help',
        ariaLabel: 'Help',
        megaMenuItems: this.supportMenuItems,
        megaMenuColumns: 1,
        hideArrow: true
      }
    ];
  }

  private redirectToLandingIfApplicable(): void {
    const url = this.router.url;
    if (url.includes('/admin/integration/') && url.includes('connect=')) {
      return;
    }
    const dashboards = this.dashboards();
    const configs = this.configDashboards() ?? {};
    const isAdminLanding = shouldLandOnAdminSettings(
      dashboards,
      this.user,
      configs
    );
    const smart = getSmartLandingDashboard(dashboards, this.user);
    if (
      isAdminLanding ||
      (smart &&
        smart.literalId !== DashboardLiteralIds.crossChannel &&
        !this.router.url.includes('/products/'))
    ) {
      this.router
        .navigateByUrl(this.landingDashboardLink())
        .catch(console.error);
    }
  }
}
